import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import "./voice-note-dialog.scss";
import micIcon from "../../../assets/icons/mic-icon.svg";

interface VoiceNoteDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (text: string) => void;
}

const VoiceNoteDialog: React.FC<VoiceNoteDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimText, setInterimText] = useState("");
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef<number | null>(null);
  const recognitionRef = useRef<any | null>(null);

  const startRecognition = () => {
    if (!(window as any).webkitSpeechRecognition) return;
    const recognition = new (window as any).webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.onresult = (event: any) => {
      let committed = "";
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) {
          committed += res[0]?.transcript || "";
        } else {
          interim = res[0]?.transcript || "";
        }
      }
      if (committed.trim()) {
        setTranscript((prev) => (prev ? prev + " " : "") + committed.trim());
        setInterimText("");
      } else {
        setInterimText(interim);
      }
    };
    recognition.onerror = (event: any) => {
      console.error("Speech recognition error", event);
    };
    recognition.start();
    recognitionRef.current = recognition;
  };

  const startRecording = () => {
    setIsRecording(true);
    setSeconds(0);
    startTimer();
    startRecognition();
  };

  const pauseRecording = () => {
    setIsRecording(false);
    setIsPaused(true);
    pauseTimer();
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {}
    }
    setInterimText("");
  };

  const resumeRecording = () => {
    setIsRecording(true);
    setIsPaused(false);
    startTimer();
    startRecognition();
  };

  const stopRecording = () => {
    setIsRecording(false);
    setIsPaused(false);
    stopTimer();
    if (interimText.trim()) {
      setTranscript((prev) => (prev ? prev + " " : "") + interimText.trim());
      setInterimText("");
    }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {}
      recognitionRef.current = null;
    }
  };

  const startTimer = () => {
    if (timerRef.current == null) {
      timerRef.current = window.setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
    }
  };

  const pauseTimer = () => {
    if (timerRef.current != null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopTimer = () => {
    if (timerRef.current != null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (open) {
      startRecording();
    }
  }, [open]);

  const handleClose = () => {
    stopRecording();
    setTranscript("");
    onClose();
  };

  const formatTime = (secs: number) => {
    const minutes = Math.floor(secs / 60);
    const seconds = secs % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm" className="voice-note-dialog">
      <DialogTitle className="dialog-title">
        Record Notes
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className="microphone-section">
          <div className="mic-visuals">
            {!isRecording ? (
              <div className="microphone-icon">
                <i className="fa fa-microphone-slash" />
              </div>
            ) : (
              <div className="microphone-icon microphone-icon-recording listening">
                <img src={micIcon} alt="Microphone" height={44} />
              </div>
            )}
            {isRecording && (
              <div className="waves" aria-hidden>
                {Array.from({ length: 20 }).map((_, idx) => (
                  <span className="wave" key={idx} />
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="timer"> {formatTime(seconds)}</div>
        <TextField
          fullWidth
          multiline
          rows={4}
          className="notes-text-field"
          placeholder="your transcribed note will appear here..."
          value={
            interimText
              ? transcript
                ? `${transcript} ${interimText}`
                : interimText
              : transcript
          }
          onChange={(e) => setTranscript(e.target.value)}
        />
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          gap: 2,
          paddingBottom: 3,
        }}
        className="buttons-group"
      >
        {isRecording ? (
          <div>
            <Button onClick={() => pauseRecording()} className="pause-button">
              <i className="fa fa-pause"></i>Pause
            </Button>
            <Button
              onClick={() => {
                stopRecording();
              }}
              className="stop-button"
            >
              <i className="fa fa-stop"></i>
              Stop
            </Button>
          </div>
        ) : isPaused ? (
          <div>
            <Button onClick={() => resumeRecording()} className="resume-button">
              <i className="fa fa-play"></i>Resume
            </Button>
            <Button
              onClick={() => {
                stopRecording();
              }}
              className="stop-button"
            >
              <i className="fa fa-stop"></i>
              Stop
            </Button>
          </div>
        ) : (
          <div>
            <Button
              onClick={() => {
                setTranscript("");
                setInterimText("");
                startRecording();
              }}
              className="record-again-button"
            >
              Record Again
            </Button>
            <Button
              className="save-note-button"
              onClick={() => {
                onSave(transcript.trim());
                setTranscript("");
              }}
              disabled={!transcript.trim()}
            >
              Save Note
            </Button>
          </div>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VoiceNoteDialog;
